import React from "react";
import { Button } from "@/components/ui/button";
import {
    CheckCircle, ShieldCheck, Package, Truck, DownloadSimple,
    Warning, ArrowRight,
} from "@phosphor-icons/react";

/**
 * ProductionNextStepBanner — the tech-portal equivalent of the admin's
 * StageHeroCard, but action-oriented: every branch renders a big CTA
 * that fires the exact action the tech should be doing at this stage.
 *
 * iter101.13 · "Continuous Flow Audit" — extends the acknowledge-gate
 * banner from iter101.8 to cover the three downstream handoffs that
 * used to require the tech to hunt through the workflow stepper:
 *
 *   1. `assigned_to_production` (or approved / submitted_to_production)
 *      + !acknowledged → amber "Review STL files & acknowledge" banner.
 *   2. `production_stage === "qc_review"` → violet "Run the QC checklist"
 *      banner. CTA scrolls to the QC stage on the workflow tab so the
 *      inspector sees the 9 check items + the "QC pass / QC fail" row.
 *   3. `production_stage === "packaging"` → primary "Log batch & package"
 *      banner. CTA scrolls to the packaging stage on the workflow tab.
 *   4. `production_stage === "packaging" || "closed"` + status still
 *      `in_production` → orange "Ship this case" banner. CTA opens the
 *      ship dialog.
 *
 * The component self-hides when none of these apply — an
 * in-progress mid-flow stage (e.g. thermoforming) shows no banner
 * because the tech is meant to be doing the work in the stepper below.
 */
export function ProductionNextStepBanner({
    c, wf, stlFiles, productionStage,
    onAcknowledge, onNavigateToStage, onOpenShipDialog,
    canShip, shipDisabledReason,
}) {
    const status = c?.status;
    const acknowledged = !!wf?.acknowledged_at;

    // 1. Acknowledge gate (pre-work)
    if (!acknowledged && (status === "assigned_to_production" || status === "submitted_to_production" || status === "approved")) {
        return (
            <div
                className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-white to-amber-50/40 p-5 shadow-sm"
                data-testid="production-detail-next-step"
            >
                <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 grid place-items-center shrink-0">
                        <CheckCircle size={22} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-700">Next step · You</div>
                        <h3 className="text-lg font-bold tracking-tight text-foreground mt-0.5">
                            Review the STL files &amp; acknowledge this case
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            {c.production_tech_name && (
                                <>Admin assigned this case to <span className="font-semibold text-foreground">{c.production_tech_name}</span>. </>
                            )}
                            Download the STL files, confirm they open cleanly and match the manufacturing spec, then click <span className="font-semibold text-foreground">Acknowledge &amp; begin</span> to start your clock. The case moves to <span className="font-semibold text-foreground">In Production</span> only after you acknowledge.
                        </p>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <Button
                                onClick={onAcknowledge}
                                disabled={stlFiles.length === 0}
                                title={stlFiles.length === 0 ? "STL files missing — cannot begin production. Contact admin." : ""}
                                className="rounded-full bg-amber-600 hover:bg-amber-700 text-white font-bold px-5 h-10"
                                data-testid="production-detail-next-step-cta"
                            >
                                <CheckCircle size={14} className="mr-1.5" weight="fill" />
                                Acknowledge &amp; begin
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => onNavigateToStage?.("stl")}
                                className="rounded-full h-10"
                                data-testid="production-detail-review-stl"
                            >
                                <DownloadSimple size={13} className="mr-1.5" weight="bold" />
                                Open STL files
                                {stlFiles.length > 0 && <span className="ml-1 opacity-70">· {stlFiles.length}</span>}
                            </Button>
                            {stlFiles.length === 0 && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1">
                                    <Warning size={11} weight="fill" />
                                    STL files missing — contact admin
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // From here down we're an acknowledged case, i.e. work is in progress.
    // Match stage → next-step banner. If nothing matches, we return null
    // and the mid-flow work happens in the ProductionWorkflowStepper below.

    // 2. QC review — inspector's cue to run the 9-point checklist.
    if (productionStage === "qc_review" && status === "in_production") {
        return (
            <div
                className="rounded-2xl border-2 border-violet-300 bg-gradient-to-br from-violet-50 via-white to-violet-50/40 p-5 shadow-sm"
                data-testid="production-detail-next-step-qc"
            >
                <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-violet-100 text-violet-700 grid place-items-center shrink-0">
                        <ShieldCheck size={22} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-violet-700">Next step · You</div>
                        <h3 className="text-lg font-bold tracking-tight text-foreground mt-0.5">
                            Run the QC checklist
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            9-point quality check before packaging: fit-check the aligners on the printed model, verify trim edges, check for cracks or ghosting, and tag any aligners that need rework. Only cases that clear <span className="font-semibold text-foreground">every</span> item can advance to packaging.
                        </p>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <Button
                                onClick={() => onNavigateToStage?.("workflow", "qc_review")}
                                className="rounded-full bg-violet-600 hover:bg-violet-700 text-white font-bold px-5 h-10"
                                data-testid="production-detail-next-step-qc-cta"
                            >
                                <ShieldCheck size={14} className="mr-1.5" weight="fill" />
                                Start QC review
                                <ArrowRight size={11} className="ml-1.5" weight="bold" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // 3. Packaging — packager's cue to log batch + label + box.
    if (productionStage === "packaging" && status === "in_production") {
        return (
            <div
                className="rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 via-white to-primary/10 p-5 shadow-sm"
                data-testid="production-detail-next-step-packaging"
            >
                <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-primary/15 text-primary grid place-items-center shrink-0">
                        <Package size={22} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary">Next step · You</div>
                        <h3 className="text-lg font-bold tracking-tight text-foreground mt-0.5">
                            Package the aligners
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            QC signed off. Label the aligners with the patient ID, box them in the sequence-labelled pouches, and log the batch number for shipment tracking. Once done, the case is ready to hand off to the courier.
                        </p>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <Button
                                onClick={() => onNavigateToStage?.("workflow", "packaging")}
                                className="rounded-full bg-primary hover:bg-primary/90 text-white font-bold px-5 h-10"
                                data-testid="production-detail-next-step-packaging-cta"
                            >
                                <Package size={14} className="mr-1.5" weight="fill" />
                                Log packaging batch
                                <ArrowRight size={11} className="ml-1.5" weight="bold" />
                            </Button>
                            {canShip && (
                                <Button
                                    variant="outline"
                                    onClick={onOpenShipDialog}
                                    className="rounded-full h-10 border-orange-300 text-orange-700 hover:bg-orange-50"
                                    data-testid="production-detail-next-step-ship-shortcut"
                                >
                                    <Truck size={13} className="mr-1.5" weight="bold" />
                                    Skip to Ship
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // 4. Ready to ship — packaging complete or workflow closed, but the
    //    case's canonical status is still `in_production` because the
    //    tech hasn't recorded tracking yet.
    if (canShip && status === "in_production" && productionStage !== "packaging") {
        // Skip if we're on the "packaging" stage — case 3 above already
        // shows a Skip-to-Ship button. This branch handles the state
        // where the tech has passed packaging (stage === "closed" or
        // similar) but hasn't entered the tracking number.
        return (
            <div
                className="rounded-2xl border-2 border-orange-300 bg-gradient-to-br from-orange-50 via-white to-orange-50/40 p-5 shadow-sm"
                data-testid="production-detail-next-step-ship"
            >
                <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-orange-100 text-orange-700 grid place-items-center shrink-0">
                        <Truck size={22} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-orange-700">Next step · You</div>
                        <h3 className="text-lg font-bold tracking-tight text-foreground mt-0.5">
                            Ship this case
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            Aligners are boxed and QC-signed. Enter the courier &amp; tracking number to close production, notify the doctor over WhatsApp, and start the delivery clock.
                        </p>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <Button
                                onClick={onOpenShipDialog}
                                disabled={!canShip}
                                title={shipDisabledReason}
                                className="rounded-full bg-orange-600 hover:bg-orange-700 text-white font-bold px-5 h-10"
                                data-testid="production-detail-next-step-ship-cta"
                            >
                                <Truck size={14} className="mr-1.5" weight="fill" />
                                Enter tracking &amp; ship
                                <ArrowRight size={11} className="ml-1.5" weight="bold" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Mid-flow (thermoforming, trimming, cleaning, etc.) — no banner.
    // Tech works from the workflow stepper directly.
    return null;
}
